import { PortfolioBalance } from 'types/DomainTypes';
import {
  fetchPortfolioBalances,
  PortfolioBalancesResponse,
} from 'views/Portfolio/components/Summary/services/fetchPortfolioBalances';
import { ToolExecutionResult, PortfolioBalanceData } from '../types';
import { formatCurrency, formatPercentage } from '../utils/formatters';

export const executeGetPortfolioBalances = async (args: Record<string, unknown>): Promise<ToolExecutionResult> => {
  try {
    const response: PortfolioBalancesResponse = await fetchPortfolioBalances();

    // Extract portfolio data from response - httpGet returns { data: PortfolioBalance[] }
    const rawData: PortfolioBalance[] = response?.data || [];

    // The API may return an "All Portfolios" summary row (with null/empty portfolioId)
    // alongside individual portfolios. Filter these out to avoid double-counting balances.
    const seenIds = new Set<string>();
    const portfolioData = rawData.filter((p) => {
      const id = p.portfolioId != null ? String(p.portfolioId).trim() : null;
      if (!id || seenIds.has(id)) {
        return false;
      }
      seenIds.add(id);
      return true;
    });

    if (portfolioData.length === 0) {
      return {
        success: true,
        data: {
          message: 'No portfolio data available. You may not have any portfolios yet.',
          balances: [],
        },
      };
    }

    // If portfolioId specified, filter to that portfolio (handle string/number comparison)
    const portfolioId = args.portfolioId != null ? String(args.portfolioId) : undefined;
    const filteredData = portfolioId
      ? portfolioData.filter((p) => String(p.portfolioId) === portfolioId)
      : portfolioData;

    const balances: PortfolioBalanceData[] = filteredData.map((balance) => ({
      portfolioName: balance.portfolioName || 'Portfolio',
      portfolioId: balance.portfolioId || '',
      currentHoldings: balance.currentHoldings || 0,
      capitalInvested: balance.capitalInvested || 0,
      profitAndLoss: balance.profitAndLoss || 0,
      profitAndLossPct: balance.profitAndLossPct || 0,
      netPosition: balance.netPosition || 0,
      netPositionPct: balance.netPositionPct || 0,
      valuation: balance.valuation || 0,
      inFlow: balance.inFlow || 0,
      outFlow: balance.outFlow || 0,
      totalMgmtFee: balance.totalMgmtFee || 0,
      netProceedsFromSales: balance.netProceedsFromSales || 0,
      cashBalance: balance.balance || 0,
      balancePending: balance.balancePending || 0,
    }));

    // Calculate totals if multiple portfolios
    const totals =
      balances.length > 1
        ? {
            totalCurrentHoldings: balances.reduce((sum, b) => sum + b.currentHoldings, 0),
            totalCapitalInvested: balances.reduce((sum, b) => sum + b.capitalInvested, 0),
            totalProfitAndLoss: balances.reduce((sum, b) => sum + b.profitAndLoss, 0),
            totalValuation: balances.reduce((sum, b) => sum + b.valuation, 0),
            totalCashBalance: balances.reduce((sum, b) => sum + b.cashBalance, 0),
            totalBalancePending: balances.reduce((sum, b) => sum + b.balancePending, 0),
          }
        : null;

    // Format individual portfolio summaries for the LLM
    const portfolioSummaries = balances.map((b) => ({
      portfolioName: b.portfolioName,
      portfolioId: b.portfolioId,
      currentHoldings: formatCurrency(b.currentHoldings),
      capitalInvested: formatCurrency(b.capitalInvested),
      valuation: formatCurrency(b.valuation),
      profitAndLoss: `${formatCurrency(b.profitAndLoss)} (${formatPercentage(b.profitAndLossPct)})`,
      netPosition: `${formatCurrency(b.netPosition)} (${formatPercentage(b.netPositionPct)})`,
      inflows: formatCurrency(b.inFlow),
      outflows: formatCurrency(b.outFlow),
      managementFees: formatCurrency(b.totalMgmtFee),
      cashBalance: formatCurrency(b.cashBalance),
      balancePending: formatCurrency(b.balancePending),
    }));

    // If single portfolio, return its data directly (matching home view behavior)
    if (balances.length === 1) {
      const p = portfolioSummaries[0];
      return {
        success: true,
        data: {
          portfolioName: p.portfolioName,
          currentHoldings: p.currentHoldings,
          capitalInvested: p.capitalInvested,
          valuation: p.valuation,
          profitAndLoss: p.profitAndLoss,
          netPosition: p.netPosition,
          inflows: p.inflows,
          outflows: p.outflows,
          managementFees: p.managementFees,
          cashBalance: p.cashBalance,
          balancePending: p.balancePending,
        },
      };
    }

    // Multiple portfolios: show each individually with optional totals
    return {
      success: true,
      data: {
        portfolios: portfolioSummaries,
        portfolioCount: balances.length,
        combinedTotals: totals
          ? {
              totalCurrentHoldings: formatCurrency(totals.totalCurrentHoldings),
              totalCapitalInvested: formatCurrency(totals.totalCapitalInvested),
              totalProfitAndLoss: formatCurrency(totals.totalProfitAndLoss),
              totalValuation: formatCurrency(totals.totalValuation),
              totalCashBalance: formatCurrency(totals.totalCashBalance),
              totalBalancePending: formatCurrency(totals.totalBalancePending),
            }
          : null,
        note: 'Showing all portfolios. Ask about a specific portfolio by name for detailed breakdown.',
      },
    };
  } catch (error) {
    const err = error as Error & { response?: { status?: number; data?: unknown } };
    let errorMessage = err.message || 'Unknown error';

    // Add more context for common error types
    if (err.response?.status === 401) {
      errorMessage = 'Authentication required. Please ensure you are logged in.';
    } else if (err.response?.status === 403) {
      errorMessage = 'Access denied to portfolio data.';
    } else if (err.message?.includes('Network Error')) {
      errorMessage = 'Network error. Please check your connection.';
    }

    return {
      success: false,
      error: `Failed to fetch portfolio balances: ${errorMessage}`,
    };
  }
};
